import { useState, useEffect } from "react";
import {
    Container, Box, Paper, Typography, Stack, Button, Divider, Alert, Chip,
    CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import QRCode from "react-qr-code";
import { useSnackbar } from "notistack";
import { useAuthContext } from "../auth/FirebaseContext";
import { getTicketByClaimTokenAPI, downloadClaimPdfApi, claimTicketAPI } from "../api/tickets";
import { fmtDate, fmtTime } from "../utils/format";

function InfoRow({ icon, label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Icon icon={icon} width={16} color="#919EAB" />
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
        </Stack>
    );
}

/**
 * Javna stranica /k/{token} — digitalna kopija karte kupljene na šalteru ili u
 * vozilu. Putnik je otvara skeniranjem QR-a sa slipa dok je štampa još čitljiva;
 * prikazuje ISTI kod koji vozač skenira, pa ekran menja izbledeli papir.
 * Bez prijave — pristup čuva nasumičan token iz linka.
 */
export default function ClaimTicketPage() {
    const { token } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { isAuthenticated, isInitialized } = useAuthContext();

    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [claiming, setClaiming] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setNotFound(false);
        getTicketByClaimTokenAPI(token)
            .then((data) => {
                if (!cancelled) setTicket(data);
            })
            .catch(() => {
                if (!cancelled) setNotFound(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const handleDownloadPdf = async () => {
        setDownloading(true);
        try {
            const response = await downloadClaimPdfApi(token);
            const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = `karta-${ticket?.ticketId || "busgo"}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error("claim pdf download", e);
            enqueueSnackbar(t("common.error", "Greška"), { variant: "error" });
        } finally {
            setDownloading(false);
        }
    };

    const handleClaim = async () => {
        setClaiming(true);
        try {
            const data = await claimTicketAPI(token);
            setTicket(data);
            enqueueSnackbar(t("claim.claimedOk"), { variant: "success" });
        } catch (e) {
            console.error("claim ticket", e);
            const serverMessage = e?.response?.data?.message;
            enqueueSnackbar(serverMessage || t("claim.claimError"), { variant: "error" });
        } finally {
            setClaiming(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>{t("claim.loading")}</Typography>
            </Container>
        );
    }

    if (notFound || !ticket) {
        return (
            <Container maxWidth="sm" sx={{ py: 6 }}>
                <Alert severity="error">{t("claim.notFound")}</Alert>
            </Container>
        );
    }

    const isOpenReturn = ticket.isReturnTicket && !ticket.journeyDate;

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                {t("claim.title")}
            </Typography>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {ticket.departureStationName} → {ticket.arrivalStationName}
                        </Typography>
                        {ticket.isReturnTicket && (
                            <Chip size="small" color="info" label={t("claim.returnTicket")} />
                        )}
                        {ticket.validated && (
                            <Chip size="small" color="success" label={t("claim.statusUsed")} />
                        )}
                        {ticket.refunded && (
                            <Chip size="small" color="warning" label={t("claim.statusRefunded")} />
                        )}
                    </Stack>

                    {ticket.purchaseTicketCount > 1 && (
                        <Typography variant="body2" color="text.secondary">
                            {t("claim.purchaseCount", { n: ticket.purchaseTicketCount })}
                        </Typography>
                    )}

                    {ticket.forQR && (
                        <Box sx={{ textAlign: "center", py: 1 }}>
                            <Box sx={{ display: "inline-block", p: 2, bgcolor: "#fff", borderRadius: 1 }}>
                                <QRCode value={String(ticket.forQR)} size={196} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {t("claim.qrHint")}
                            </Typography>
                        </Box>
                    )}

                    <Divider />

                    <Stack spacing={1}>
                        {isOpenReturn ? (
                            <InfoRow icon="mdi:calendar" label={t("claim.date")} value={t("claim.openReturn")} />
                        ) : (
                            <InfoRow
                                icon="mdi:calendar"
                                label={t("claim.date")}
                                value={ticket.journeyDate
                                    ? `${fmtDate(ticket.journeyDate)} ${fmtTime(ticket.journeyDate)}`
                                    : null}
                            />
                        )}
                        <InfoRow icon="mdi:seat" label={t("claim.seat")} value={ticket.seat} />
                        <InfoRow icon="mdi:bus" label={t("claim.line")} value={ticket.lineName} />
                        <InfoRow icon="mdi:domain" label={t("claim.operator")} value={ticket.operatorName} />
                        <InfoRow
                            icon="mdi:clock-outline"
                            label={t("claim.validUntil")}
                            value={ticket.validUntil ? fmtDate(ticket.validUntil) : null}
                        />
                    </Stack>

                    <Button
                        variant="contained"
                        startIcon={<Icon icon="mdi:file-pdf-box" />}
                        onClick={handleDownloadPdf}
                        disabled={downloading}
                    >
                        {t("claim.downloadPdf")}
                    </Button>

                    <Alert severity="info">{t("claim.saveHint")}</Alert>

                    {ticket.claimed ? (
                        <Alert
                            severity="success"
                            action={isAuthenticated ? (
                                <Button color="inherit" size="small" onClick={() => navigate("/moje-karte")}>
                                    {t("claim.goToMyTickets")}
                                </Button>
                            ) : null}
                        >
                            {t("claim.claimedInfo")}
                        </Alert>
                    ) : isInitialized && (
                        <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {t("claim.ctaTitle")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {t("claim.ctaText")}
                            </Typography>
                            {isAuthenticated ? (
                                <Button
                                    variant="outlined"
                                    startIcon={<Icon icon="mdi:ticket-confirmation-outline" />}
                                    onClick={handleClaim}
                                    disabled={claiming}
                                >
                                    {t("claim.addToMyTickets")}
                                </Button>
                            ) : (
                                <Button variant="outlined" onClick={() => navigate("/?login=1")}>
                                    {t("claim.ctaButton")}
                                </Button>
                            )}
                        </Box>
                    )}
                </Stack>
            </Paper>
        </Container>
    );
}
